import maplibregl from 'maplibre-gl';
import BaseProduct from './BaseProduct';
import LayerManager from './LayerManager';
import { ProductId, StreamflowStation, StreamflowSiteFilter, StreamflowColorMode } from '../types';
import store from '../state';
import { streamflowModal } from '../ui/panels/StreamflowModal';

const SOURCE_ID = 'streamflow-source';
const CIRCLES_LAYER_ID = 'streamflow-circles';
const LABELS_LAYER_ID = 'streamflow-labels';

type EffectiveColorMode = 'percent_normal' | 'raw' | 'temperature';

export class StreamflowLayer extends BaseProduct {
  public readonly id: ProductId = 'streamflow';
  private hoverPopup: maplibregl.Popup | null = null;
  private currentSiteFilter: StreamflowSiteFilter = 'both';
  private currentMode: StreamflowColorMode = 'percent_normal';
  private currentMinCfs: number = 0;
  private tempMapEnabled: boolean = false;
  private labelsEnabled: boolean = true;
  private currentUnits: 'imperial' | 'metric' = 'imperial';
  private lastMtimeEpoch: number | null = null;
  private interactionsBound: boolean = false;

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);

    const init = store.getState();
    this.currentSiteFilter = init.streamflowSiteFilter || 'both';
    this.currentMode = init.streamflowColorMode || 'percent_normal';
    this.currentMinCfs = init.streamflowMinCfs;
    this.tempMapEnabled = init.streamflowTempMapEnabled;
    this.labelsEnabled = init.streamflowLabelsEnabled !== false;
    this.currentUnits = init.units || 'imperial';
    this.lastMtimeEpoch = init.manifest?.streamflow?.mtime_epoch ?? null;

    this.map.on('style.load', () => {
      if (this.active) {
        void this.initSourceAndLayers();
      }
    });

    store.subscribe((state) => {
      const nextSiteFilter = state.streamflowSiteFilter || 'both';
      const nextLabels = state.streamflowLabelsEnabled !== false;
      const nextUnits = state.units || 'imperial';
      const nextMtime = state.manifest?.streamflow?.mtime_epoch ?? null;

      if (nextMtime !== null && nextMtime !== this.lastMtimeEpoch) {
        this.lastMtimeEpoch = nextMtime;
        const src = this.map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        if (src && typeof src.setData === 'function') {
          src.setData(this.buildGeoJsonUrl());
        }
      }

      if (
        this.currentSiteFilter !== nextSiteFilter ||
        this.currentMode !== state.streamflowColorMode ||
        this.currentMinCfs !== state.streamflowMinCfs ||
        this.tempMapEnabled !== state.streamflowTempMapEnabled ||
        this.labelsEnabled !== nextLabels ||
        this.currentUnits !== nextUnits
      ) {
        this.currentSiteFilter = nextSiteFilter;
        this.currentMode = state.streamflowColorMode;
        this.currentMinCfs = state.streamflowMinCfs;
        this.tempMapEnabled = state.streamflowTempMapEnabled;
        this.labelsEnabled = nextLabels;
        this.currentUnits = nextUnits;
        if (this.active) {
          this.updateLayerStyles();
        }
      }

      if (state.streamflowEnabled !== this.active) {
        if (state.streamflowEnabled) {
          this.activate();
        } else {
          this.deactivate();
        }
      }
    });
  }

  private getEffectiveColorMode(): EffectiveColorMode {
    if (this.currentMode === 'temperature' || this.tempMapEnabled) {
      return 'temperature';
    }
    if (this.currentMode === 'raw' || this.currentMode === 'absolute') {
      return 'raw';
    }
    return 'percent_normal';
  }

  protected async onActivate(): Promise<void> {
    if (!this.map.getSource(SOURCE_ID) || !this.map.getLayer(CIRCLES_LAYER_ID)) {
      await this.initSourceAndLayers();
    } else {
      this.updateLayerStyles();
      this.setVisibility(true);
    }
  }

  protected onDeactivate(): void {
    this.setVisibility(false);
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    // Streamflow and reservoir telemetry is real-time observational data
  }

  private buildGeoJsonUrl(): string {
    const manifest = store.getState().manifest;
    const apiServer = manifest?.api_server || '';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const cacheBust = manifest?.streamflow?.mtime_epoch || Math.floor(Date.now() / 900000);

    let cleanPath = `${tileRoot}/observed/streamflow/streamflow.json`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) {
      cleanPath = cleanPath.substring(1);
    }
    const base = apiServer ? `${apiServer}/${cleanPath}` : cleanPath;
    return `${base}?_t=${cacheBust}`;
  }

  private getIsReservoirExpression(): any {
    return [
      'any',
      ['==', ['get', 'site_type'], 'reservoir'],
      ['!=', ['get', 'storage_acre_ft'], null],
      ['!=', ['get', 'elevation_ft'], null]
    ];
  }

  private buildFilterExpression(): any {
    const tiers = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.streamflow?.zoom_tiers) || {};
    const r2Zoom = Number(tiers.rank2_zoom) || 5.5;
    const r3Zoom = Number(tiers.rank3_zoom) || 7.0;
    const r4Zoom = Number(tiers.rank4_zoom) || 8.5;

    // When viewing only Lakes & Reservoirs (~800-1000 features nationwide), reveal +1 tier earlier
    const maxRankStep = this.currentSiteFilter === 'reservoirs'
      ? ['step', ['zoom'], 2, r2Zoom, 3, r3Zoom, 4]
      : ['step', ['zoom'], 1, r2Zoom, 2, r3Zoom, 3, r4Zoom, 4];

    const rankFilter: any = [
      'any',
      ['==', ['get', 'status'], 'flood'],
      ['<=', ['coalesce', ['get', 'rank'], 4], maxRankStep]
    ];

    const isReservoirExpr = this.getIsReservoirExpression();
    const clauses: any[] = [rankFilter];

    if (this.currentSiteFilter === 'rivers') {
      clauses.push(['!', isReservoirExpr]);
    } else if (this.currentSiteFilter === 'reservoirs') {
      clauses.push(isReservoirExpr);
    }

    if (this.currentMinCfs > 0) {
      clauses.push([
        'any',
        isReservoirExpr,
        ['>=', ['coalesce', ['get', 'cfs'], 0], this.currentMinCfs]
      ]);
    }

    return clauses.length === 1 ? clauses[0] : ['all', ...clauses];
  }

  private buildLabelFilterExpression(): any {
    const baseFilter = this.buildFilterExpression();
    const effMode = this.getEffectiveColorMode();

    if (effMode === 'temperature') {
      return [
        'all',
        baseFilter,
        ['!=', ['get', this.currentUnits === 'metric' ? 'water_temp_c' : 'water_temp_f'], null]
      ];
    }

    if (effMode === 'percent_normal') {
      return [
        'all',
        baseFilter,
        [
          'any',
          ['!=', ['get', 'pct_normal'], null],
          ['!=', ['get', 'cfs'], null],
          ['!=', ['get', 'storage_acre_ft'], null],
          ['!=', ['get', 'elevation_ft'], null]
        ]
      ];
    }

    return [
      'all',
      baseFilter,
      [
        'any',
        ['!=', ['get', 'cfs'], null],
        ['!=', ['get', 'storage_acre_ft'], null],
        ['!=', ['get', 'elevation_ft'], null]
      ]
    ];
  }

  private buildLabelTextExpression(): any {
    const effMode = this.getEffectiveColorMode();
    const isReservoirExpr = this.getIsReservoirExpression();

    if (effMode === 'temperature') {
      if (this.currentUnits === 'metric') {
        return ['concat', ['to-string', ['round', ['get', 'water_temp_c']]], '°C'];
      }
      return ['concat', ['to-string', ['round', ['get', 'water_temp_f']]], '°F'];
    }

    const rawReservoirLabel: any = [
      'case',
      ['all', ['!=', ['get', 'storage_acre_ft'], null], ['>=', ['coalesce', ['get', 'storage_acre_ft'], 0], 1000000]],
      ['concat', ['to-string', ['/', ['round', ['/', ['coalesce', ['get', 'storage_acre_ft'], 0], 100000]], 10]], 'M af'],
      ['all', ['!=', ['get', 'storage_acre_ft'], null], ['>=', ['coalesce', ['get', 'storage_acre_ft'], 0], 1000]],
      ['concat', ['to-string', ['round', ['/', ['coalesce', ['get', 'storage_acre_ft'], 0], 1000]]], 'k af'],
      ['!=', ['get', 'storage_acre_ft'], null],
      ['concat', ['to-string', ['round', ['coalesce', ['get', 'storage_acre_ft'], 0]]], ' af'],
      ['!=', ['get', 'elevation_ft'], null],
      this.currentUnits === 'metric'
        ? ['concat', ['to-string', ['round', ['*', ['coalesce', ['get', 'elevation_ft'], 0], 0.3048]]], ' m']
        : ['concat', ['to-string', ['round', ['coalesce', ['get', 'elevation_ft'], 0]]], ' ft'],
      ''
    ];

    const rawRiverLabel: any = [
      'case',
      ['!=', ['get', 'cfs'], null],
      this.currentUnits === 'metric'
        ? ['concat', ['to-string', ['/', ['round', ['*', ['coalesce', ['get', 'cfs'], 0], 0.283168]], 10]], ' m³/s']
        : ['concat', ['to-string', ['round', ['coalesce', ['get', 'cfs'], 0]]], ' cfs'],
      ''
    ];

    if (effMode === 'percent_normal') {
      return [
        'case',
        ['!=', ['get', 'pct_normal'], null],
        ['concat', ['to-string', ['round', ['coalesce', ['get', 'pct_normal'], 0]]], '%'],
        isReservoirExpr,
        rawReservoirLabel,
        rawRiverLabel
      ];
    }

    return [
      'case',
      isReservoirExpr,
      rawReservoirLabel,
      rawRiverLabel
    ];
  }

  private getRadiusExpression(): any {
    const effMode = this.getEffectiveColorMode();
    const isReservoirExpr = this.getIsReservoirExpression();
    const isMutedNoTemp: any = effMode === 'temperature'
      ? ['==', ['get', 'water_temp_f'], null]
      : ['literal', false];

    const resRadiusZ3: any = [
      'case',
      ['!=', ['get', 'storage_acre_ft'], null],
      ['max', 4.2, ['min', 7.2, ['*', 0.95, ['log10', ['max', 100, ['coalesce', ['get', 'storage_acre_ft'], 100]]]]]],
      4.6
    ];
    const resRadiusZ8: any = [
      'case',
      ['!=', ['get', 'storage_acre_ft'], null],
      ['max', 7.8, ['min', 13.5, ['*', 1.85, ['log10', ['max', 100, ['coalesce', ['get', 'storage_acre_ft'], 100]]]]]],
      8.8
    ];

    const rivRadiusZ3: any = ['max', 3.0, ['min', 9.5, ['*', 1.75, ['log10', ['max', 1, ['coalesce', ['get', 'cfs'], 1]]]]]];
    const rivRadiusZ8: any = ['max', 5.0, ['min', 17.5, ['*', 3.15, ['log10', ['max', 1, ['coalesce', ['get', 'cfs'], 1]]]]]];

    return [
      'interpolate',
      ['linear'],
      ['zoom'],
      3,
      [
        'case',
        isMutedNoTemp,
        2.3,
        isReservoirExpr,
        resRadiusZ3,
        rivRadiusZ3
      ],
      8,
      [
        'case',
        isMutedNoTemp,
        4.2,
        isReservoirExpr,
        resRadiusZ8,
        rivRadiusZ8
      ]
    ];
  }

  private getOpacityExpression(): any {
    const effMode = this.getEffectiveColorMode();
    if (effMode === 'temperature') {
      return [
        'case',
        ['==', ['get', 'water_temp_f'], null],
        0.46,
        0.94
      ];
    }
    return 0.93;
  }

  private getStrokeColorExpression(): any {
    const effMode = this.getEffectiveColorMode();
    const isReservoirExpr = this.getIsReservoirExpression();
    if (effMode === 'temperature') {
      return [
        'case',
        ['==', ['get', 'water_temp_f'], null],
        'rgba(15, 23, 42, 0.55)',
        isReservoirExpr,
        '#e0f2fe',
        '#090d16'
      ];
    }
    return [
      'case',
      isReservoirExpr,
      '#e0f2fe',
      '#090d16'
    ];
  }

  private getStrokeWidthExpression(): any {
    const effMode = this.getEffectiveColorMode();
    const isReservoirExpr = this.getIsReservoirExpression();
    if (effMode === 'temperature') {
      return [
        'case',
        ['==', ['get', 'water_temp_f'], null],
        0.8,
        isReservoirExpr,
        1.85,
        1.3
      ];
    }
    return [
      'case',
      isReservoirExpr,
      1.85,
      1.3
    ];
  }

  private getColorExpression(): any {
    const effMode = this.getEffectiveColorMode();
    const sfCfg = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.streamflow) || {};
    const isReservoirExpr = this.getIsReservoirExpression();

    if (effMode === 'temperature') {
      const wt = Array.isArray(sfCfg.water_temp_f_thresholds) && sfCfg.water_temp_f_thresholds.length >= 4
        ? sfCfg.water_temp_f_thresholds
        : [45, 55, 65, 75];
      return [
        'case',
        ['==', ['get', 'water_temp_f'], null],
        '#475569', // Muted neutral slate gray for gauges without temperature sensors
        [
          'step',
          ['coalesce', ['get', 'water_temp_f'], 0],
          '#1d4ed8',        // < 45°F (Cold Deep Blue)
          Number(wt[0]), '#06b6d4', // 45-55°F (Cyan / Trout Optimal)
          Number(wt[1]), '#10b981', // 55-65°F (Emerald Green)
          Number(wt[2]), '#f59e0b', // 65-75°F (Amber Warm)
          Number(wt[3]), '#ef4444'  // > 75°F (Thermal Stress Red)
        ]
      ];
    }

    if (effMode === 'percent_normal') {
      const pn = sfCfg.percent_normal_thresholds || {};
      const tMuchBelow = Number(pn.much_below) || 25;
      const tBelow = Number(pn.below_normal) || 75;
      const tAbove = Number(pn.above_normal) || 125;
      const tMuchAbove = Number(pn.much_above) || 200;

      return [
        'case',
        ['!=', ['get', 'pct_normal'], null],
        [
          'step',
          ['coalesce', ['get', 'pct_normal'], 100],
          '#A0522D',       // < 25% of Normal (Much Below / Drought Brown)
          tMuchBelow, '#DAA520', // 25 - 75% of Normal (Below Normal Amber)
          tBelow, '#10b981',     // 75 - 125% of Normal (Near Normal Emerald)
          tAbove, '#06b6d4',     // 125 - 200% of Normal (Above Normal Cyan)
          tMuchAbove, '#3b82f6', // 200 - 350% of Normal (High Blue)
          350, '#9333ea'         // > 350% of Normal (Extreme High / Flood Violet)
        ],
        [
          'match',
          ['get', 'status'],
          'low', '#A0522D',
          'below_normal', '#DAA520',
          'normal', '#10b981',
          'above_normal', '#06b6d4',
          'high', '#3b82f6',
          'flood', '#9333ea',
          '#64748b'        // Unrated / No historical normal record
        ]
      ];
    }

    // Raw Flow Rate (CFS) for Rivers / Raw Storage (acre-ft) or Level for Lakes & Reservoirs
    const cfsT = Array.isArray(sfCfg.raw_cfs_thresholds) && sfCfg.raw_cfs_thresholds.length >= 4
      ? sfCfg.raw_cfs_thresholds
      : [50, 500, 2500, 10000];
    const storT = Array.isArray(sfCfg.raw_storage_acre_ft_thresholds) && sfCfg.raw_storage_acre_ft_thresholds.length >= 4
      ? sfCfg.raw_storage_acre_ft_thresholds
      : [25000, 150000, 750000, 3000000];

    return [
      'case',
      isReservoirExpr,
      [
        'case',
        ['!=', ['get', 'storage_acre_ft'], null],
        [
          'step',
          ['coalesce', ['get', 'storage_acre_ft'], 0],
          '#7dd3fc',               // < 25k ac-ft
          Number(storT[0]), '#06b6d4', // 25k - 150k ac-ft
          Number(storT[1]), '#22c55e', // 150k - 750k ac-ft
          Number(storT[2]), '#f59e0b', // 750k - 3M ac-ft
          Number(storT[3]), '#ec4899'  // > 3M ac-ft (Major Reservoirs: Mead, Powell, Shasta)
        ],
        ['!=', ['get', 'elevation_ft'], null],
        '#38bdf8',                 // Elevation-only natural lakes (e.g. Crater Lake, Tahoe)
        '#64748b'
      ],
      [
        'case',
        ['!=', ['get', 'cfs'], null],
        [
          'step',
          ['coalesce', ['get', 'cfs'], 0],
          '#7dd3fc',              // < 50 cfs
          Number(cfsT[0]), '#06b6d4', // 50 - 500 cfs
          Number(cfsT[1]), '#22c55e', // 500 - 2,500 cfs
          Number(cfsT[2]), '#f59e0b', // 2,500 - 10,000 cfs
          Number(cfsT[3]), '#ec4899'  // > 10,000 cfs
        ],
        '#64748b'
      ]
    ];
  }

  private async initSourceAndLayers(): Promise<void> {
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: this.buildGeoJsonUrl(),
        cluster: false,
        maxzoom: 12,
        buffer: 128,
        tolerance: 0
      });
    }

    if (!this.map.getLayer(CIRCLES_LAYER_ID)) {
      this.map.addLayer({
        id: CIRCLES_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: this.buildFilterExpression(),
        paint: {
          'circle-radius': this.getRadiusExpression() as any,
          'circle-color': this.getColorExpression() as any,
          'circle-stroke-color': this.getStrokeColorExpression() as any,
          'circle-stroke-width': this.getStrokeWidthExpression() as any,
          'circle-opacity': this.getOpacityExpression() as any,
          'circle-stroke-opacity': 0.9
        }
      });
    }

    const tiers = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.streamflow?.zoom_tiers) || {};
    const labelMinZoom = Number(tiers.label_min_zoom) || 8.5;

    if (!this.map.getLayer(LABELS_LAYER_ID)) {
      this.map.addLayer({
        id: LABELS_LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        minzoom: labelMinZoom,
        filter: this.buildLabelFilterExpression(),
        layout: {
          'text-field': this.buildLabelTextExpression(),
          'text-font': ['Noto Sans Regular'],
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            8.5, 10.5,
            12, 12.0
          ] as any,
          'symbol-sort-key': ['coalesce', ['get', 'rank'], 4] as any,
          'text-variable-anchor': ['bottom', 'top', 'right', 'left'],
          'text-radial-offset': 0.75,
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'text-padding': 4
        },
        paint: {
          'text-color': '#f8fafc',
          'text-halo-color': 'rgba(2, 6, 23, 0.94)',
          'text-halo-width': 1.75,
          'text-halo-blur': 0.25
        }
      });
    }

    if (!this.interactionsBound) {
      this.setupInteractions();
      this.interactionsBound = true;
    }

    this.updateLayerStyles();
    this.setVisibility(true);
  }

  private updateLayerStyles(): void {
    if (!this.map.getLayer(CIRCLES_LAYER_ID)) return;

    this.map.setPaintProperty(CIRCLES_LAYER_ID, 'circle-color', this.getColorExpression());
    this.map.setPaintProperty(CIRCLES_LAYER_ID, 'circle-radius', this.getRadiusExpression());
    this.map.setPaintProperty(CIRCLES_LAYER_ID, 'circle-opacity', this.getOpacityExpression());
    this.map.setPaintProperty(CIRCLES_LAYER_ID, 'circle-stroke-color', this.getStrokeColorExpression());
    this.map.setPaintProperty(CIRCLES_LAYER_ID, 'circle-stroke-width', this.getStrokeWidthExpression());
    this.map.setFilter(CIRCLES_LAYER_ID, this.buildFilterExpression());

    if (this.map.getLayer(LABELS_LAYER_ID)) {
      this.map.setFilter(LABELS_LAYER_ID, this.buildLabelFilterExpression());
      this.map.setLayoutProperty(LABELS_LAYER_ID, 'text-field', this.buildLabelTextExpression());
      this.map.setLayoutProperty(
        LABELS_LAYER_ID,
        'visibility',
        this.active && this.labelsEnabled ? 'visible' : 'none'
      );
    }
  }

  private setVisibility(visible: boolean): void {
    const val = visible ? 'visible' : 'none';
    if (this.map.getLayer(CIRCLES_LAYER_ID)) {
      this.map.setLayoutProperty(CIRCLES_LAYER_ID, 'visibility', val);
    }
    if (this.map.getLayer(LABELS_LAYER_ID)) {
      this.map.setLayoutProperty(LABELS_LAYER_ID, 'visibility', visible && this.labelsEnabled ? 'visible' : 'none');
    }
  }

  private setupInteractions(): void {
    this.hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 10,
      className: 'streamflow-hover-popup'
    });

    const onEnter = (e: maplibregl.MapLayerMouseEvent) => {
      this.map.getCanvas().style.cursor = 'pointer';
      if (!e.features || e.features.length === 0) return;

      const f = e.features[0];
      const props = f.properties as any;
      const coords = (f.geometry as any).coordinates.slice();

      const hasCfs = props.cfs !== null && props.cfs !== undefined;
      const hasHeight = props.height !== null && props.height !== undefined;
      const hasTemp = props.water_temp_f !== null && props.water_temp_f !== undefined;
      const hasStorage = props.storage_acre_ft !== null && props.storage_acre_ft !== undefined;
      const hasElev = props.elevation_ft !== null && props.elevation_ft !== undefined;
      const hasPctNorm = props.pct_normal !== null && props.pct_normal !== undefined;
      const hasPctCap = props.pct_capacity !== null && props.pct_capacity !== undefined;
      const hasElevDiff = props.elev_diff_ft !== null && props.elev_diff_ft !== undefined;

      const isMetric = this.currentUnits === 'metric';
      const cfsText = hasCfs
        ? (isMetric ? `${(Number(props.cfs) * 0.0283168).toFixed(1)} m³/s` : `${Number(props.cfs).toLocaleString()} cfs`)
        : null;
      const heightText = hasHeight && !hasElev
        ? (isMetric ? `${(Number(props.height) * 0.3048).toFixed(2)} m` : `${props.height} ft`)
        : null;
      const tempText = hasTemp
        ? (isMetric ? `${props.water_temp_c}°C (${props.water_temp_f}°F)` : `${props.water_temp_f}°F (${props.water_temp_c}°C)`)
        : null;
      const storageText = hasStorage ? `${Math.round(Number(props.storage_acre_ft)).toLocaleString()} ac-ft` : null;
      const elevText = hasElev
        ? (isMetric ? `${(Number(props.elevation_ft) * 0.3048).toFixed(1)} m MSL` : `${Number(props.elevation_ft).toLocaleString(undefined, { maximumFractionDigits: 2 })} ft MSL`)
        : null;

      const isReservoir = props.site_type === 'reservoir' || hasStorage || hasElev;
      const agency = props.agency || 'USGS';

      const statusLabels: Record<string, { label: string; color: string }> = {
        low: { label: 'Much Below Normal (<25%)', color: '#d97706' },
        below_normal: { label: 'Below Normal (25-75%)', color: '#fbbf24' },
        normal: { label: 'Near Normal (75-125%)', color: '#34d399' },
        above_normal: { label: 'Above Normal (125-200%)', color: '#22d3ee' },
        high: { label: 'Much Above Normal (>200%)', color: '#60a5fa' },
        flood: { label: 'Extreme High / Flood Stage', color: '#c084fc' },
        unknown: { label: isReservoir ? 'Monitored Lake / Reservoir' : 'Active Stream Gauge', color: '#94a3b8' }
      };
      const stInfo = statusLabels[props.status] || statusLabels.unknown;

      let normSummary = stInfo.label;
      if (hasPctNorm) {
        const diffSuffix = hasElevDiff
          ? ` (${Number(props.elev_diff_ft) >= 0 ? '+' : ''}${Number(props.elev_diff_ft).toFixed(1)} ft vs normal)`
          : '';
        normSummary = `${Math.round(Number(props.pct_normal))}% of Normal${diffSuffix}`;
      }

      const titleIcon = isReservoir ? '🏞️' : '🌊';
      const metricsHtml = `
        <div style="display: flex; gap: 10px 14px; font-size: 12px; margin-bottom: 5px; flex-wrap: wrap;">
          ${storageText ? `<div><strong>Storage:</strong> <span style="color: #60A5FA;">${storageText}</span>${hasPctCap ? ` <span style="color:#94a3b8;">(${Math.round(Number(props.pct_capacity))}% full)</span>` : ''}</div>` : ''}
          ${elevText ? `<div><strong>Level:</strong> <span style="color: #A7F3D0;">${elevText}</span></div>` : ''}
          ${cfsText ? `<div><strong>${isReservoir ? 'Outflow' : 'Flow'}:</strong> <span style="color: #34D399;">${cfsText}</span></div>` : ''}
          ${heightText ? `<div><strong>Stage:</strong> <span style="color: #FBBF24;">${heightText}</span></div>` : ''}
          ${tempText ? `<div><strong>Temp:</strong> <span style="color: #F472B6;">${tempText}</span></div>` : ''}
        </div>
      `;

      const html = `
        <div style="font-family: sans-serif; padding: 7px 10px; color: #fff; background: rgba(15,23,42,0.96); border: 1px solid rgba(148,163,184,0.22); border-radius: 7px; box-shadow: 0 6px 16px rgba(0,0,0,0.55); min-width: 215px; max-width: 310px;">
          <div style="font-weight: 700; font-size: 13px; color: #60A5FA; margin-bottom: 2px; line-height: 1.3;">${titleIcon} ${props.name}</div>
          <div style="font-size: 11px; color: #94A3B8; margin-bottom: 6px;">${agency} ${isReservoir ? 'Lake / Reservoir' : 'River Gauge'} · ${props.id}</div>
          ${metricsHtml}
          <div style="font-size: 11px; font-weight: 600; color: ${stInfo.color}; margin-top: 3px;">${normSummary}</div>
        </div>
      `;

      this.hoverPopup!.setLngLat(coords).setHTML(html).addTo(this.map);
    };

    const onLeave = () => {
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    };

    const onClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (!e.features || e.features.length === 0) return;
      const f = e.features[0];
      const props = f.properties as any;
      const station: StreamflowStation = {
        id: props.id,
        agency: props.agency ?? 'USGS',
        name: props.name,
        lat: (f.geometry as any).coordinates[1],
        lon: (f.geometry as any).coordinates[0],
        site_type: props.site_type,
        cfs: props.cfs ?? null,
        height: props.height ?? null,
        water_temp_c: props.water_temp_c ?? null,
        water_temp_f: props.water_temp_f ?? null,
        storage_acre_ft: props.storage_acre_ft ?? null,
        elevation_ft: props.elevation_ft ?? null,
        pct_normal: props.pct_normal ?? null,
        pct_capacity: props.pct_capacity ?? null,
        normal_cfs: props.normal_cfs ?? null,
        normal_storage_acre_ft: props.normal_storage_acre_ft ?? null,
        normal_elevation_ft: props.normal_elevation_ft ?? null,
        elev_diff_ft: props.elev_diff_ft ?? null,
        external_url: props.external_url ?? null,
        status: props.status,
        pct: props.pct,
        time: props.time
      };

      store.update({ streamflowSelectedStation: station });
      streamflowModal.open(station);
    };

    for (const lid of [CIRCLES_LAYER_ID, LABELS_LAYER_ID]) {
      this.map.on('mouseenter', lid, onEnter);
      this.map.on('mouseleave', lid, onLeave);
      this.map.on('click', lid, onClick);
    }
  }
}

export default StreamflowLayer;
